import { AppModuleCode, PermissionCode, type AuthUser } from '@gb-ticket/shared-types';
import { BookOpenIcon, LayersIcon, TicketIcon, type LucideIcon } from 'lucide-react';
import { createElement, type ReactNode } from 'react';

import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import { HELPDESK_ROOT } from '@/products/helpdesk/helpdesk-paths';

export type AgentNavItem = {
  to: string;
  label: string;
  end?: boolean;
  /** Primary work destinations only; secondary groups use a letter when collapsed. */
  icon?: ReactNode;
};

function navIcon(Icon: LucideIcon): ReactNode {
  return createElement(Icon, { 'aria-hidden': true, className: 'size-4', strokeWidth: 1.75 });
}

export type AgentNavGroupId = 'work' | 'ops' | 'master' | 'admin';

export type AgentNavGroup = {
  id: AgentNavGroupId;
  label: string | null;
  items: AgentNavItem[];
};

/** Logged-out users keep default-on optional links that have no permission gate. */
function moduleOn(user: AuthUser | null | undefined, code: AppModuleCode): boolean {
  return !user || isModuleEnabled(user, code);
}

export function agentNavGroups(user: AuthUser | null | undefined): AgentNavGroup[] {
  const can = (permission: PermissionCode) => hasPermission(user, permission);

  const groups: AgentNavGroup[] = [
    {
      id: 'work',
      label: null,
      items: [
        ...(can(PermissionCode.TICKETS_READ)
          ? [{ to: '/helpdesk/tickets', label: 'Tickets', icon: navIcon(TicketIcon) }]
          : []),
        ...(can(PermissionCode.TICKETS_READ_ALL) && moduleOn(user, AppModuleCode.QUEUES)
          ? [
              {
                to: '/helpdesk/warteschlangen',
                label: 'Warteschlangen',
                icon: navIcon(LayersIcon),
              },
            ]
          : []),
        ...(can(PermissionCode.KB_READ) && moduleOn(user, AppModuleCode.KNOWLEDGE)
          ? [{ to: '/helpdesk/wissen', label: 'Wissen', icon: navIcon(BookOpenIcon) }]
          : []),
      ],
    },
    {
      id: 'ops',
      label: 'Betrieb',
      items: [
        ...(can(PermissionCode.TICKETS_READ_ALL) && moduleOn(user, AppModuleCode.CHANGES)
          ? [{ to: '/helpdesk/changes', label: 'Änderungen' }]
          : []),
        ...(can(PermissionCode.APPROVALS_MANAGE) && moduleOn(user, AppModuleCode.APPROVALS)
          ? [{ to: '/helpdesk/freigaben', label: 'Freigaben' }]
          : []),
        ...(can(PermissionCode.SLA_MANAGE) && moduleOn(user, AppModuleCode.SLA)
          ? [{ to: '/helpdesk/sla', label: 'SLA' }]
          : []),
        ...(can(PermissionCode.CSAT_READ) && moduleOn(user, AppModuleCode.CSAT)
          ? [{ to: '/helpdesk/auswertung', label: 'Auswertung' }]
          : []),
      ],
    },
    {
      id: 'master',
      label: 'Stammdaten',
      items: [
        ...(can(PermissionCode.ASSETS_READ) && moduleOn(user, AppModuleCode.ASSETS)
          ? [{ to: '/helpdesk/assets', label: 'Inventar' }]
          : []),
      ],
    },
    {
      id: 'admin',
      label: 'Verwaltung',
      items: [
        ...(can(PermissionCode.AUTOMATION_READ) && moduleOn(user, AppModuleCode.AUTOMATION)
          ? [{ to: '/helpdesk/automatisierung', label: 'Automatisierung' }]
          : []),
        ...(can(PermissionCode.INTEGRATIONS_MANAGE) && moduleOn(user, AppModuleCode.INTEGRATIONS)
          ? [{ to: '/helpdesk/integrationen', label: 'Integrationen' }]
          : []),
        ...(moduleOn(user, AppModuleCode.STATUS)
          ? [{ to: '/helpdesk/status', label: 'Systemstatus' }]
          : []),
        ...(can(PermissionCode.SETTINGS_MANAGE)
          ? [
              { to: '/helpdesk/einstellungen/kategorien', label: 'Katalog' },
              { to: '/helpdesk/einstellungen/system', label: 'Module' },
            ]
          : []),
      ],
    },
  ];

  return groups.filter((group) => group.items.length > 0);
}

export function agentNavItems(user: AuthUser | null | undefined): AgentNavItem[] {
  return agentNavGroups(user).flatMap((group) => group.items);
}

/** Best matching primary-nav label for the current path (nested routes included). */
export function agentNavSectionTitle(pathname: string, groups: AgentNavGroup[]): string {
  const items = groups.flatMap((group) => group.items);
  const match = items
    .filter((item) =>
      item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`),
    )
    .sort((left, right) => right.to.length - left.to.length)[0];
  if (match) {
    return match.label;
  }
  if (pathname === '/' || pathname === '' || pathname === HELPDESK_ROOT) {
    return 'Übersicht';
  }
  if (pathname.startsWith('/einstellungen')) {
    return 'Einstellungen';
  }
  return 'Helpdesk';
}
